import type { ReactNode } from 'react';

/**
 * Label-above-value, the old panel's `InfoBox`. Used by every card in the
 * drawer, which is why it lives on its own: the source exports it from
 * `OrderOtherInfo` and imports it into two siblings.
 *
 * Title 14px/400 grey, value 16px/600 black — transcribed from `titleText` /
 * `infoText`. Both accept nodes, because several call sites put a countdown or
 * a link where the plain string would go.
 */
export function LegacyInfoBox({ title, info }: { title: ReactNode; info: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 sm:gap-1">
      <span className="text-[14px] font-normal text-[var(--lg-grey-500)]">{title}</span>
      <span className="text-[16px] font-semibold text-[var(--lg-fg)]">{info}</span>
    </div>
  );
}

/** A grey rounded chip — the source's `labelBox`. */
export function LegacyChip({
  children,
  as = 'div',
  href,
  onClick,
}: {
  children: ReactNode;
  as?: 'div' | 'a';
  href?: string;
  onClick?: () => void;
}) {
  const className =
    'inline-flex max-w-full items-center gap-1 rounded-lg bg-[var(--lg-grey-100)] px-2 py-1 text-[16px] text-[var(--lg-fg)]';

  if (as === 'a') {
    return (
      <a href={href} className={`${className} cursor-pointer`}>
        {children}
      </a>
    );
  }

  return onClick ? (
    <button type="button" onClick={onClick} className={`${className} cursor-pointer`}>
      {children}
    </button>
  ) : (
    <div className={className}>{children}</div>
  );
}
